import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { VideoSummary, VideoVisibility } from '@streamr/shared';
import { VideoGrid } from '../components/VideoGrid';
import { Button, ErrorState, Field, Spinner, inputClass } from '../components/ui';
import { flattenPages, useChannelVideos } from '../api/hooks/useDiscovery';
import { useDeleteVideo, useUpdateVideo, useVideo } from '../api/hooks/useVideos';
import { useAuth } from '../auth/AuthProvider';
import { errorMessage } from '../api/errors';

/** The owner's list of their own videos, including the ones still processing. */
export const StudioPage = (): JSX.Element => {
  const { user } = useAuth();
  const videos = useChannelVideos(user?.channel.handle ?? '');

  if (user === null) return <p className="text-sm text-ink-300">Sign in to see your videos.</p>;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Your videos</h1>
        <Link to="/upload">
          <Button>Upload</Button>
        </Link>
      </div>
      <VideoGrid
        videos={flattenPages<VideoSummary>(videos.data)}
        isLoading={videos.isLoading}
        error={videos.error}
        hasNextPage={videos.hasNextPage}
        isFetchingNextPage={videos.isFetchingNextPage}
        fetchNextPage={() => void videos.fetchNextPage()}
        showChannel={false}
        emptyTitle="You have not uploaded anything yet"
      />
    </div>
  );
};

const VISIBILITIES: VideoVisibility[] = ['PRIVATE', 'UNLISTED', 'PUBLIC'];

export const VideoEditPage = (): JSX.Element => {
  const { videoId = '' } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const video = useVideo(videoId);
  const update = useUpdateVideo(videoId);
  const remove = useDeleteVideo(videoId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [visibility, setVisibility] = useState<VideoVisibility>('PRIVATE');
  const [loaded, setLoaded] = useState(false);

  // Seed the form once, from the server. Re-seeding on every refetch would
  // discard whatever the owner had half-typed.
  useEffect(() => {
    if (video.data === undefined || loaded) return;
    setTitle(video.data.title);
    setDescription(video.data.description);
    setTagsText(video.data.tags.join(', '));
    setVisibility(video.data.visibility);
    setLoaded(true);
  }, [video.data, loaded]);

  if (video.isLoading) return <Spinner label="Loading video" />;
  if (video.isError) return <ErrorState error={video.error} />;
  if (video.data === undefined) return <ErrorState error={new Error('Video not found')} />;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    update.mutate({
      title,
      description,
      visibility,
      tags: tagsText
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag) => tag !== ''),
    });
  };

  return (
    <form onSubmit={submit} className="mx-auto flex max-w-xl flex-col gap-5">
      <h1 className="text-xl font-semibold">Edit video</h1>

      <Field label="Title" htmlFor="title">
        <input id="title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={150} className={inputClass} />
      </Field>

      <Field label="Description" htmlFor="description">
        <textarea
          id="description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={5}
          maxLength={5000}
          className={inputClass}
        />
      </Field>

      <Field label="Tags" htmlFor="tags" hint="Comma separated.">
        <input id="tags" value={tagsText} onChange={(event) => setTagsText(event.target.value)} className={inputClass} />
      </Field>

      <Field label="Visibility" htmlFor="visibility">
        <select
          id="visibility"
          value={visibility}
          onChange={(event) => setVisibility(event.target.value as VideoVisibility)}
          className={inputClass}
        >
          {VISIBILITIES.map((value) => (
            <option key={value} value={value}>
              {value.charAt(0) + value.slice(1).toLowerCase()}
            </option>
          ))}
        </select>
      </Field>

      {update.isError && <p className="text-sm text-red-300">{errorMessage(update.error)}</p>}
      {update.isSuccess && <p className="text-sm text-emerald-300">Saved.</p>}

      <div className="flex gap-2">
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button
          type="button"
          variant="danger"
          disabled={remove.isPending}
          onClick={() => {
            if (!window.confirm('Delete this video? This cannot be undone.')) return;
            remove.mutate(undefined, { onSuccess: () => navigate('/studio') });
          }}
        >
          Delete
        </Button>
      </div>
    </form>
  );
};
